import React from 'react';
import { Link } from 'react-router-dom';

const SectionHeading = ({ children }) => (
  <h2 style={{ fontSize: 20, marginBottom: 12 }}>{children}</h2>
);

const SubHeading = ({ children }) => (
  <h3 style={{ fontSize: 16, marginBottom: 8, marginTop: 16 }}>{children}</h3>
);

function Privacy() {
  return (
    <div className="container pnp-legal" style={{ maxWidth: 800, paddingTop: 40, paddingBottom: 60 }}>
      <Link to="/" style={{ color: 'var(--primary)', fontSize: 14 }}>
        &larr; Back to Home
      </Link>

      <h1 style={{ marginTop: 24, marginBottom: 8 }}>Privacy Policy</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32 }}>
        Last updated: April 27, 2026
      </p>

      <div style={{ lineHeight: 1.7, color: 'var(--text)' }}>
        <section style={{ marginBottom: 32 }}>
          <SectionHeading>1. Who We Are</SectionHeading>
          <p>
            Pick &lsquo;N Pray (the &ldquo;Service&rdquo;) is operated by [OPERATOR] (the
            &ldquo;Operator,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). This Privacy
            Policy describes what information we collect, how we use it, who we share it with, and
            the choices you have. It applies to the Pick &lsquo;N Pray website, mobile applications,
            and related features. Use of the Service is also subject to our{' '}
            <Link to="/terms" style={{ color: 'var(--primary)' }}>Terms of Service</Link>.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>2. Information We Collect</SectionHeading>

          <SubHeading>Information you provide directly</SubHeading>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li>
              <strong>Account information:</strong> username, email address, and password (stored
              as a one-way hash, never in plain text).
            </li>
            <li>
              <strong>Profile information (optional):</strong> display name, first and last name,
              country, bio, profile photo, social handles (Instagram, X), and Venmo handle (if you
              choose to provide it for member-to-member payouts that occur outside the Service).
            </li>
            <li>
              <strong>Pool activity:</strong> pools you create or join, your fight predictions
              (&ldquo;Picks&rdquo;), method picks, scores, badges earned, and pool-specific stats.
            </li>
            <li>
              <strong>Corner Chat messages:</strong> text messages you send in pool chat rooms.
              Messages are automatically deleted thirty (30) days after they are posted. Messages
              you send are also deleted when you delete your account.
            </li>
          </ul>

          <SubHeading>Information collected automatically</SubHeading>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li>
              <strong>Usage data:</strong> pages visited, features used, and time spent in the
              Service. Collected via Vercel Analytics.
            </li>
            <li>
              <strong>Device and connection data:</strong> browser type, operating system, IP
              address, approximate location (derived from IP), and access times. Used for security
              and basic anti-abuse.
            </li>
            <li>
              <strong>Auth tokens:</strong> a session token stored in your browser&apos;s local
              storage (or the device&apos;s secure storage on native apps) so you stay logged in.
            </li>
          </ul>

          <SubHeading>What we don&apos;t collect</SubHeading>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li>
              We do not collect payment-card information. We do not process or hold any money.
            </li>
            <li>
              We do not use Apple&apos;s IDFA or similar advertising identifiers, and we do not
              track you across other apps or websites.
            </li>
            <li>
              We do not knowingly collect any information from anyone under eighteen (18) years of
              age. See Section 8.
            </li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>3. How We Use Information</SectionHeading>
          <p style={{ marginBottom: 12 }}>We use the information we collect to:</p>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li>Provide, maintain, and operate the Service, including authentication, scoring, leaderboards, and Corner Chat;</li>
            <li>Compute and display badges, ranks, and historical performance;</li>
            <li>Communicate with you about the Service (account-related messages, security notices, important updates);</li>
            <li>Detect, prevent, and respond to security incidents, fraud, abuse, or violations of our Terms;</li>
            <li>Improve the Service and inform product decisions in aggregate;</li>
            <li>Comply with applicable law and respond to lawful legal process.</li>
          </ul>
          <p>
            We do not use your information to make automated decisions that produce legal effects
            or similarly significant effects about you.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>4. How Information Is Shared</SectionHeading>

          <SubHeading>Other users in your pools</SubHeading>
          <p>
            Your username, display name, profile photo, country flag, picks (after lock), scores,
            badges, and rank are visible to other members of pools you join. Corner Chat messages
            are visible to every member of that pool. The pool commissioner can additionally see
            members&apos; email addresses and Venmo handles (if provided) in order to manage the pool
            and any external monetary arrangement among members.
          </p>

          <SubHeading>Service providers</SubHeading>
          <p style={{ marginBottom: 8 }}>
            We share data with third parties strictly to operate the Service:
          </p>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li><strong>Supabase</strong> &mdash; database hosting, authentication, and realtime infrastructure for Corner Chat;</li>
            <li><strong>Render</strong> &mdash; backend server hosting;</li>
            <li><strong>Vercel</strong> &mdash; web frontend hosting and Vercel Analytics;</li>
            <li><strong>Apple App Store</strong> and <strong>Google Play Store</strong> &mdash; mobile app distribution;</li>
            <li><strong>Email providers</strong> &mdash; transactional emails such as account-related messages.</li>
          </ul>
          <p>
            Each provider has its own privacy practices and security commitments. We share only
            the data needed for them to perform their function.
          </p>

          <SubHeading>Legal and safety</SubHeading>
          <p>
            We may disclose information if we believe in good faith it&apos;s necessary to comply with
            law, valid legal process, or a government request; to enforce our Terms; or to protect
            the rights, property, or safety of users or the public.
          </p>

          <SubHeading>Business transfers</SubHeading>
          <p>
            If we&apos;re ever involved in a merger, acquisition, financing, reorganization, or sale of
            assets, your information may be transferred as part of that transaction, subject to
            this Privacy Policy.
          </p>

          <SubHeading>What we do NOT do</SubHeading>
          <p>
            We do not sell your personal information. We do not share your personal information
            with advertising networks. We do not use your data to train third-party AI models.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>5. Data Retention</SectionHeading>
          <ul style={{ marginLeft: 24, marginBottom: 12 }}>
            <li>
              <strong>Corner Chat messages</strong> are automatically deleted thirty (30) days
              after posting via a daily cleanup job.
            </li>
            <li>
              <strong>Account data</strong> is retained while your account is active. When you
              delete your account (Settings &rsaquo; Delete my account), we scrub identifying
              fields (email, names, profile photo, social handles, Venmo handle, country, bio)
              within minutes. Your historical picks and pool memberships are retained in
              anonymized form (you appear as &ldquo;Deleted user&rdquo;) so other members&apos; pool history
              and standings remain accurate.
            </li>
            <li>
              <strong>Server logs</strong> (request logs, error logs) are retained for up to
              ninety (90) days for security and debugging.
            </li>
            <li>
              <strong>Analytics</strong> data retention is governed by the analytics provider
              (Vercel Analytics).
            </li>
            <li>
              We may retain certain information longer if required by law or to resolve disputes
              or enforce our agreements.
            </li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>6. Your Choices and Rights</SectionHeading>

          <SubHeading>Access and edit</SubHeading>
          <p>
            You can view and edit most of your account information directly in the Service:
            Profile &rsaquo; Settings.
          </p>

          <SubHeading>Delete your account</SubHeading>
          <p>
            You can permanently delete your account from inside the app: Profile &rsaquo;
            Settings &rsaquo; Delete my account. Deletion is immediate and cannot be undone.
            Personally identifying information is scrubbed; your past picks remain anonymized so
            other members&apos; pool history is preserved.
          </p>

          <SubHeading>Region-specific rights</SubHeading>
          <p>
            Depending on where you live, you may have additional rights under applicable privacy
            law (for example, GDPR in the EU/UK, CCPA/CPRA in California). These can include the
            right to access, correct, or delete personal information we hold about you, the right
            to data portability, and the right to object to or restrict certain processing. To
            exercise any of these rights, email us at{' '}
            <a href="mailto:kyle@picknpray.com" style={{ color: 'var(--primary)' }}>kyle@picknpray.com</a>.
            We will respond within the timeframe required by your local law. We will not
            discriminate against you for exercising your rights.
          </p>

          <SubHeading>Marketing</SubHeading>
          <p>
            We do not currently send marketing emails. If we ever do, you will be able to opt out
            via a link in the email or by contacting us.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>7. Security</SectionHeading>
          <p>
            We use industry-standard practices to protect your information, including TLS
            encryption in transit, password hashing with bcrypt, role-scoped database keys, and
            row-level security on sensitive tables. No system is perfectly secure. You are
            responsible for keeping your password confidential and for the security of your
            device. If you believe your account has been compromised, contact us immediately at{' '}
            <a href="mailto:kyle@picknpray.com" style={{ color: 'var(--primary)' }}>kyle@picknpray.com</a>.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>8. Children</SectionHeading>
          <p>
            The Service is not directed to and is not intended for anyone under eighteen (18)
            years of age. We do not knowingly collect personal information from anyone under 18.
            If you believe we have collected information from a child under 18, contact us at{' '}
            <a href="mailto:kyle@picknpray.com" style={{ color: 'var(--primary)' }}>kyle@picknpray.com</a>{' '}
            and we will promptly delete it.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>9. International Users</SectionHeading>
          <p>
            The Service is operated from the United States. If you access the Service from outside
            the U.S., your information will be transferred to and processed in the U.S., where
            data-protection laws may differ from those in your country. By using the Service, you
            consent to that transfer.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>10. Cookies and Similar Technologies</SectionHeading>
          <p>
            We use a small number of strictly necessary cookies and equivalent storage (such as
            local storage and device-secure storage) to keep you logged in and remember your
            selected pool. We use Vercel Analytics, which uses cookieless, privacy-friendly
            measurement. We do not use third-party advertising or cross-site tracking cookies.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>11. Push Notifications and Permissions (Mobile)</SectionHeading>
          <p>
            If we add native push notifications in a future version, the mobile app will request
            your permission before subscribing you, and you can revoke that permission at any time
            in your device settings. The mobile app does not currently request access to your
            camera, photos, contacts, location, or microphone.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>12. Changes to This Policy</SectionHeading>
          <p>
            We may update this Privacy Policy from time to time. When we do, we&apos;ll update the
            &ldquo;Last updated&rdquo; date at the top. Material changes will be reasonably highlighted
            in-app or by email. Your continued use of the Service after a change constitutes
            acceptance of the revised policy.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <SectionHeading>13. Contact</SectionHeading>
          <p>
            Questions, requests, or complaints about this Privacy Policy? Email{' '}
            <a href="mailto:kyle@picknpray.com" style={{ color: 'var(--primary)' }}>kyle@picknpray.com</a>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default Privacy;
